import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export type SpaceRole = 'visitor' | 'editor' | 'admin';

export interface SpaceMember {
  spaceId: string;
  userId: string;
  role: SpaceRole;
  email?: string;
}

/**
 * CRUD for per-space role grants — the delegation primitive that lets a
 * customer Super-Admin (or a Space ADMIN) hand out EDITOR / ADMIN coverage
 * on a Space subtree to other users. Backed by `/cms/spaces/:spaceId/...`.
 */
@Injectable({ providedIn: 'root' })
export class SpaceMembersService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _members = signal<SpaceMember[]>([]);
  private readonly _loading = signal(false);
  private currentSpaceId: string | null = null;

  readonly members = this._members.asReadonly();
  readonly loading = this._loading.asReadonly();

  async loadMembers(spaceId: string): Promise<void> {
    this._loading.set(true);
    this.currentSpaceId = spaceId;
    try {
      const data = await lastValueFrom(
        this.http.get<SpaceMember[]>(`${this.api}/ecms/spaces/${spaceId}/claims`),
      );
      this._members.set(data);
    } finally {
      this._loading.set(false);
    }
  }

  async addMember(spaceId: string, userId: string, role: SpaceRole): Promise<void> {
    await lastValueFrom(
      this.http.post(`${this.api}/ecms/spaces/${spaceId}/members`, { userId, role }),
    );
    if (this.currentSpaceId === spaceId) {
      await this.loadMembers(spaceId);
    }
  }

  async updateMemberRole(spaceId: string, userId: string, role: SpaceRole): Promise<void> {
    await lastValueFrom(
      this.http.patch(`${this.api}/ecms/spaces/${spaceId}/members/${userId}`, { role }),
    );
    if (this.currentSpaceId === spaceId) {
      await this.loadMembers(spaceId);
    }
  }

  async removeMember(spaceId: string, userId: string): Promise<void> {
    await lastValueFrom(
      this.http.delete(`${this.api}/ecms/spaces/${spaceId}/members/${userId}`),
    );
    if (this.currentSpaceId === spaceId) {
      await this.loadMembers(spaceId);
    }
  }

  clear(): void {
    this._members.set([]);
    this.currentSpaceId = null;
  }
}
